import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getDefaultAddress, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { fetchBidLimitBalanceIfNeeded } from '@/redux/modules/bidLimit/bidLimit.actions';
import { fetchCatalogRegistrationsForUserByIdIfNeeded } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import {
    getItemSummaryCatalogId,
    getItemSummaryIndex,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummarySellerId,
    getItemSummaryShippingProviderId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchItemShippingEstimateIfNeeded } from '@/redux/modules/shipping/shipping.actions';
import TEST_HOUSES from '@/utils/testHouses';

import { fetchCatalogAnnouncementIfNeeded } from '../catalog/announcement/catalogAnnouncement.actions';
import { fetchCatalogsIfNeeded } from '../catalog/catalogs/catalog.actions';
import { fetchCatalogDetailsIfNeeded } from '../catalog/detail/catalogDetail.actions';
import { fetchCatalogItemSummaries } from '../catalog/items/catalogItems.actions';
import { loadRelatedGeoAreaByLocation } from '../geoArea/geoArea';
import { getHasRequiredShippingDimensions, getItemDetailAgentId } from '../itemDetail';
import { fetchListingAgentsIfNeeded } from '../listingAgent';
import { fetchSellersIfNeeded } from '../seller';
import { fetchSellersDetailsIfNeeded } from '../sellerDetail';
import { fetchSellerFollowerCountIfNeeded } from '../sellerFollowerCount';
import { fetchSellerRatingSummaryByIdIfNeeded } from '../sellerRatings';

export const fetchDataForUpcomingItemPage = createAsyncThunk<
    Promise<unknown>,
    {
        itemId: number;
    }
>('la/domain/itemPage/fetchDataForUpcomingItemPage', async ({ itemId }, { dispatch, getState }) => {
    let state = getState();
    const isLoggedIn = isUserLoggedIn(state);
    const catalogId = getItemSummaryCatalogId(state, itemId);
    const index = getItemSummaryIndex(state, itemId);
    const isPassed = getItemSummaryIsPassed(state, itemId);
    const isSold = getItemSummaryIsSold(state, itemId);
    const sellerId = getItemSummarySellerId(state, itemId);
    const shippingProviderId = getItemSummaryShippingProviderId(state, itemId);

    const agentId = getItemDetailAgentId(state, itemId);

    // we need to load the previous item plus the next 20 items for the item page
    const startingIndexForCatalogCarousel = index === 0 ? 0 : index - 1;

    const query: any = {
        index: startingIndexForCatalogCarousel,
        pageSize: 22,
    };
    if (TEST_HOUSES.includes(sellerId)) {
        query.preview = true;
    }

    const promises: Promise<unknown>[] = [
        dispatch(
            fetchCatalogAnnouncementIfNeeded({
                catalogId,
                houseId: sellerId,
            })
        ),
        dispatch(fetchCatalogDetailsIfNeeded([catalogId])),
        dispatch(fetchCatalogItemSummaries({ catalogId, query })),
        dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] })),
        dispatch(fetchListingAgentsIfNeeded([agentId])),
        dispatch(fetchSellerFollowerCountIfNeeded([sellerId])),
        dispatch(fetchSellerRatingSummaryByIdIfNeeded(sellerId)),
        dispatch(fetchSellersDetailsIfNeeded([sellerId])),
        dispatch(loadRelatedGeoAreaByLocation(catalogId)),
    ];

    if (isLoggedIn) {
        promises.push(dispatch(fetchBidLimitBalanceIfNeeded({ catalogId, itemId })));
        promises.push(
            dispatch(
                fetchCatalogRegistrationsForUserByIdIfNeeded({
                    catalogIds: [catalogId],
                })
            )
        );

        await dispatch(fetchSellersIfNeeded([sellerId]));
        state = getState();

        const bidderAddress = getDefaultAddress(state);
        const hasRequiredShippingDimensions = getHasRequiredShippingDimensions(state, itemId);
        const isItemClosed = isSold || isPassed;

        if (
            Boolean(bidderAddress) &&
            Boolean(shippingProviderId) &&
            hasRequiredShippingDimensions &&
            Boolean(bidderAddress.postalCode) &&
            !isItemClosed
        ) {
            promises.push(
                dispatch(
                    fetchItemShippingEstimateIfNeeded({
                        countryCode: bidderAddress.countryCode,
                        itemId,
                        postalCode: bidderAddress.postalCode,
                    })
                )
            );
        }
    }

    return Promise.all(promises);
});
